import { FunctionComponent, useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { faUserPlus } from "@fortawesome/pro-regular-svg-icons";

import { FeedPerson, FeedRunCard } from "@signalist/shared-types";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";
import { AnchoredPopover } from "@/pages/Private/pages/Contacts/components/LeadPanelActions/AnchoredPopover";
import { OrganizationProfile } from "@/pages/Private/pages/List/schema/organization-profiles";
import {
	useAddExistingProspectToListMutation,
	useGetOrganizationProfilesQuery,
} from "@/pages/Private/redux/list/list.api";

import { feedCardKey } from "../../utils/feedCardKey";
import { FeedPendingAdds } from "../../utils/useFeedPendingAdds";

import {
	RELATIONSHIP_DOT_CLASS,
	getInitials,
	getScoreRingDisplay,
	getStrongestRelationship,
} from "./runCardUtils";

interface PersonSwitchMenuProps {
	card: FeedRunCard;
	/** The shown lead — the anchor the organization-profiles query keys on. */
	listPersonId: number;
	people: FeedPerson[];
	selectedListPersonId: number | null;
	anchorEl: HTMLElement | null;
	/** The page's add-lead owner; it outlives both this menu and the card (PREC-5041 review S1/S3). */
	pendingAdds: FeedPendingAdds;
	/** This menu's open — a callback from an earlier menu must not close a later one. */
	interactionId: number;
	onClose: (interactionId: number) => void;
	onSelect: (listPersonId: number) => void;
	onAddError: () => void;
	onAddPerson: () => void;
}

/** Space kept between the menu and the viewport edge. */
const MENU_MARGIN_PX = 6;
/** Never taller than this share of the viewport, even when there is more room. */
const MENU_MAX_VIEWPORT_RATIO = 0.7;
/** Below this the menu flips above the trigger when there is more room there. */
const MENU_MIN_HEIGHT_PX = 240;

/**
 * Fit the menu inside the viewport (PREC-5041 review S2): `AnchoredPopover` only clamps
 * horizontally, so a trigger near the bottom would otherwise push "Add Person" off-screen.
 * Flip above the trigger when that side has more room, and cap the height to the chosen
 * side minus a margin; the rows scroll inside.
 */
const getMenuBounds = (
	anchorEl: HTMLElement | null
): { placement: "bottom" | "top"; maxHeight: number } => {
	const viewportHeight = window.innerHeight;
	const rect = anchorEl?.getBoundingClientRect();
	const below = rect ? viewportHeight - rect.bottom - 2 * MENU_MARGIN_PX : viewportHeight;
	const above = rect ? rect.top - 2 * MENU_MARGIN_PX : 0;
	const placement = below < MENU_MIN_HEIGHT_PX && above > below ? "top" : "bottom";
	const available = Math.max(0, placement === "bottom" ? below : above);

	return {
		placement,
		maxHeight: Math.min(viewportHeight * MENU_MAX_VIEWPORT_RATIO, available),
	};
};

/**
 * Person menu of the entity header (D5, PREC-5041) — an {@link AnchoredPopover}
 * so it escapes the card's overflow and never triggers card selection. Rows, in
 * order: the card's leads (select), the company's Profiles that are not leads
 * yet (add as a new lead, never replace), Profiles already on the list but
 * hidden by the feed's filters (inert), and "Add Person" (the Profiles tab's
 * company-scoped search). The profiles query only runs while this menu is open.
 */
export const PersonSwitchMenu: FunctionComponent<PersonSwitchMenuProps> = ({
	card,
	listPersonId,
	people,
	selectedListPersonId,
	anchorEl,
	pendingAdds,
	interactionId,
	onClose,
	onSelect,
	onAddError,
	onAddPerson,
}) => {
	const { t } = useTranslation();
	const { data, isLoading, isError } = useGetOrganizationProfilesQuery(listPersonId);
	const [addExistingProspect] = useAddExistingProspectToListMutation();
	const [addingProspectId, setAddingProspectId] = useState<number | null>(null);
	const [bounds, setBounds] = useState(() => getMenuBounds(anchorEl));

	useLayoutEffect(() => {
		const update = () =>
			setBounds((previous) => {
				const next = getMenuBounds(anchorEl);

				// A scroll event fires for every row the user scrolls; keep the same object so
				// an unchanged anchor does not re-render the whole row list.
				return previous.placement === next.placement && previous.maxHeight === next.maxHeight
					? previous
					: next;
			});

		window.addEventListener("resize", update);
		window.addEventListener("scroll", update, true);

		return () => {
			window.removeEventListener("resize", update);
			window.removeEventListener("scroll", update, true);
		};
	}, [anchorEl]);

	const leadProspectIds = new Set(
		people.map((person) => person.prospectId).filter((id): id is number => id !== null)
	);
	const profiles = data?.profiles ?? [];
	const addableProfiles = profiles.filter(
		(profile) => !profile.isOnCurrentList && !leadProspectIds.has(profile.prospectId)
	);
	const hiddenListedProfiles = profiles.filter(
		(profile) => profile.isOnCurrentList && !leadProspectIds.has(profile.prospectId)
	);

	// A succeeded add stays pending until the refetched payload stops offering that prospect,
	// so the reopened menu cannot send it again before the row turns "on the list" (review S1).
	const addableProspectIds = addableProfiles.map((profile) => profile.prospectId);

	useEffect(() => {
		if (!data) {
			return;
		}

		pendingAdds.syncAddableProfiles(card.listId, addableProspectIds);
	}, [addableProspectIds, card.listId, data, pendingAdds]);

	const handleAdd = async (profile: OrganizationProfile) => {
		// The owner's ref makes this hold before React re-renders, so a fast double click —
		// or the same row clicked after Escape and a reopen — cannot send a second add (§24c).
		if (!pendingAdds.beginAdd(card.listId, profile.prospectId)) {
			return;
		}

		setAddingProspectId(profile.prospectId);

		try {
			// No `listPersonId`: that optional id is the replace path, and this always adds a
			// new lead beside the current one (PREC-5041).
			const added = await addExistingProspect({
				listId: card.listId,
				prospectId: profile.prospectId,
			}).unwrap();

			pendingAdds.settleAdd({
				cardKey: feedCardKey(card),
				listId: card.listId,
				prospectId: profile.prospectId,
				added: {
					listPersonId: added.id,
					prospectId: added.prospect?.id ?? profile.prospectId,
				},
			});
			onClose(interactionId);
		} catch {
			pendingAdds.settleAdd({
				cardKey: feedCardKey(card),
				listId: card.listId,
				prospectId: profile.prospectId,
			});
			onAddError();
		} finally {
			setAddingProspectId(null);
		}
	};

	return (
		<AnchoredPopover
			anchorEl={anchorEl}
			placement={bounds.placement}
			width={280}
			onClose={() => onClose(interactionId)}
		>
			{/* stopPropagation: the popover portals outside the card's DOM but React
			    still bubbles through the tree — a row click must not select the card. */}
			<div
				className="flex flex-col gap-0.5 overflow-y-auto rounded-lg border border-border-light bg-white p-1 shadow-lg"
				data-feed-noselect="true"
				data-testid="feed-person-switch-menu"
				style={{ maxHeight: bounds.maxHeight }}
				onClick={(event) => event.stopPropagation()}
			>
				{people.map((person) => {
					const ring = getScoreRingDisplay(person.score);
					const relationship = getStrongestRelationship(person.relationship);
					const isSelected = person.listPersonId === selectedListPersonId;

					return (
						<button
							key={person.listPersonId}
							className={classNames(
								"flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-background-light",
								isSelected ? "bg-light-blue" : ""
							)}
							data-testid={`feed-person-option-${person.listPersonId}`}
							type="button"
							onClick={() => {
								onSelect(person.listPersonId);
								onClose(interactionId);
							}}
						>
							<span
								className={classNames(
									"grid h-6 w-6 shrink-0 place-items-center rounded-full border-[1.5px] bg-white text-[9px] font-bold",
									ring ? ring.className : "border-border-light text-text-tertiary"
								)}
							>
								{ring ? ring.label : getInitials(person.name)}
							</span>
							<span
								className={classNames(
									"h-2 w-2 shrink-0 rounded-full",
									relationship ? RELATIONSHIP_DOT_CLASS[relationship] : "bg-transparent"
								)}
							/>
							<span className="min-w-0 flex-1 truncate text-xs font-semibold text-text">
								{person.name}
							</span>
							<span className="max-w-[110px] truncate text-[11px] text-text-tertiary">
								{person.role}
							</span>
						</button>
					);
				})}

				{isLoading && (
					<div
						className="px-2 py-1.5 text-[11px] text-text-tertiary"
						data-testid="feed-person-switch-loading"
					>
						{t("feed.runCard.switchMenu.loading")}
					</div>
				)}
				{isError && (
					<div
						className="px-2 py-1.5 text-[11px] text-status-error"
						data-testid="feed-person-switch-error"
					>
						{t("feed.runCard.switchMenu.loadError")}
					</div>
				)}

				{addableProfiles.map((profile) => {
					const isAdding =
						addingProspectId === profile.prospectId ||
						pendingAdds.isAddPending(card.listId, profile.prospectId);
					// State lands a render later, so an add already in flight (also after a reopen)
					// keeps every row disabled until it settles.
					const isAddPending = addingProspectId !== null || isAdding;

					return (
						<button
							key={profile.prospectId}
							className={classNames(
								"flex items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-background-light",
								isAddPending ? "cursor-not-allowed opacity-60" : "cursor-pointer"
							)}
							data-testid={`feed-person-add-${profile.prospectId}`}
							disabled={isAddPending}
							type="button"
							onClick={() => handleAdd(profile)}
						>
							<span className="grid h-6 w-6 shrink-0 place-items-center rounded-full border-[1.5px] border-border-light bg-white text-[9px] font-bold text-text-tertiary">
								{getInitials(profile.name ?? "")}
							</span>
							<span className="min-w-0 flex-1 truncate text-xs font-semibold text-text">
								{profile.name}
							</span>
							<span className="max-w-[110px] truncate text-[11px] text-text-tertiary">
								{profile.title}
							</span>
							<span className="shrink-0 text-[11px] font-semibold text-brand-light">
								{isAdding
									? t("feed.runCard.switchMenu.addingIndication")
									: t("feed.runCard.switchMenu.addAsLead")}
							</span>
						</button>
					);
				})}

				{hiddenListedProfiles.map((profile) => (
					<div
						key={profile.prospectId}
						className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left"
						data-testid={`feed-person-on-list-${profile.prospectId}`}
					>
						<span className="grid h-6 w-6 shrink-0 place-items-center rounded-full border-[1.5px] border-border-light bg-white text-[9px] font-bold text-text-tertiary">
							{getInitials(profile.name ?? "")}
						</span>
						<span className="min-w-0 flex-1 truncate text-xs font-semibold text-text">
							{profile.name}
						</span>
						<span className="shrink-0 text-[10px] text-text-tertiary">
							{t("feed.runCard.switchMenu.onListHidden")}
						</span>
					</div>
				))}

				<button
					className="mt-0.5 flex cursor-pointer items-center gap-2 rounded-md border-t border-border-light px-2 py-1.5 text-left text-xs font-semibold text-brand-light hover:bg-background-light"
					data-testid="feed-person-add-person"
					type="button"
					onClick={() => {
						onClose(interactionId);
						onAddPerson();
					}}
				>
					<Icon className="h-3 w-3" icon={faUserPlus} />
					{t("feed.runCard.switchMenu.addPerson")}
				</button>
			</div>
		</AnchoredPopover>
	);
};
